import { useState, useEffect } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  List,
  ListItemButton,
  Avatar,
  Divider,
  Paper,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ZoomableImage from "./ZoomableImage";

const CompareDialog = ({ open, onClose, selectedImage, images = [] }) => {
  const [secondary, setSecondary] = useState(null);

  useEffect(() => {
    if (images?.length > 1) {
      setSecondary(images[1]?.image);
    }
  }, [images]);

  const referenceImageObj = images.find((img) => img.image === selectedImage);
  const comparisonImageObj = images.find((img) => img.image === secondary);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <CompareArrowsIcon sx={{ mr: 1.5, color: "primary.main" }} />
          <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
            Side-by-Side Comparison
          </Typography>
          <IconButton edge="end" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* LEFT IMAGE LIST */}
        <Box
          sx={{
            width: 220,
            borderRight: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            bgcolor: "grey.50",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              px: 2,
              pt: 1.5,
              pb: 0.5,
              color: "primary.main",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            Select Comparison Image
          </Typography>

          <Divider />

          <List dense disablePadding sx={{ overflowY: "auto", flex: 1 }}>
            {images.map((img) => (
              <ListItemButton
                key={img.id}
                selected={secondary === img.image}
                onClick={() => setSecondary(img.image)}
                sx={{
                  py: 1,
                  "&.Mui-selected": {
                    borderLeft: "3px solid",
                    borderColor: "primary.main",
                  },
                }}
              >
                <Avatar src={img.image} variant="rounded" sx={{ width: 44, height: 44 }} />
                <Typography sx={{ ml: 1.5 }} noWrap variant="body2">
                  {img.photo_type?.name_str || img?.photo_type_name || "Image"}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* IMAGE PANELS */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: 2,
            p: 2,
            overflow: "hidden",
          }}
        >
          {/* PRIMARY IMAGE */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 1 }}>
              <Chip label="Reference Image" size="small" color="primary" variant="outlined" />
              {(referenceImageObj?.photo_type?.name_str || referenceImageObj?.photo_type_name) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {referenceImageObj?.photo_type?.name_str || referenceImageObj?.photo_type_name}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              <ZoomableImage src={selectedImage} />
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* SECONDARY IMAGE */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 1 }}>
              <Chip label="Comparison Image" size="small" color="secondary" variant="outlined" />
              {(comparisonImageObj?.photo_type?.name_str ||
                comparisonImageObj?.photo_type_name) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {comparisonImageObj?.photo_type?.name_str || comparisonImageObj?.photo_type_name}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              {secondary ? (
                <ZoomableImage src={secondary} />
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.50",
                  }}
                >
                  <Typography color="text.secondary">Select an image to compare</Typography>
                </Paper>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CompareDialog;
